import { useInfiniteQuery, useMutation, useQuery, useQueryClient, InfiniteData } from "@tanstack/react-query";
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { mediaService } from "../services/mediaService";
import {
  Media,
  MediaIndex,
  MediaUploaderFilter,
  mediaUploaderFilterKey,
  MultipleMediaUploadResult,
} from "../types/media";
import { DEFAULT_MEDIA_SORT, MediaSortOptions } from "../types/mediaSort";
import { handleApiError, getErrorMessage } from "../utils/errorHandler";
import { hydrateMediaFromIndex, overlayMediaById, withoutMediaIndexItem } from "../utils/hydrateMediaFromIndex";
import { albumKeys } from "./useAlbumsQuery";
import { authService } from "../services/authService";
import { albumDebug, albumDebugDuration } from "../utils/albumDebug";

export const mediaKeys = {
  all: ["media"] as const,
  lists: () => [...mediaKeys.all, "list"] as const,
  listBase: (ownerId: string, albumId: string) => [...mediaKeys.lists(), ownerId, albumId] as const,
  list: (ownerId: string, albumId: string, sort: MediaSortOptions, uploaderFilter?: MediaUploaderFilter | null) =>
    [
      ...mediaKeys.listBase(ownerId, albumId),
      sort.sortBy,
      sort.sortOrder,
      mediaUploaderFilterKey(uploaderFilter),
    ] as const,
  count: (ownerId: string, albumId: string, uploaderFilter?: MediaUploaderFilter | null) =>
    [...mediaKeys.listBase(ownerId, albumId), "count", mediaUploaderFilterKey(uploaderFilter)] as const,
  indexes: () => [...mediaKeys.all, "index"] as const,
  indexBase: (ownerId: string, albumId: string) => [...mediaKeys.indexes(), ownerId, albumId] as const,
  index: (ownerId: string, albumId: string, sort: MediaSortOptions, uploaderFilter?: MediaUploaderFilter | null) =>
    [
      ...mediaKeys.indexBase(ownerId, albumId),
      sort.sortBy,
      sort.sortOrder,
      mediaUploaderFilterKey(uploaderFilter),
    ] as const,
  uploaders: (ownerId: string, albumId: string) => [...mediaKeys.all, "uploaders", ownerId, albumId] as const,
  duplicateFingerprints: (ownerId: string, albumId: string) =>
    [...mediaKeys.all, "duplicate-fingerprints", ownerId, albumId] as const,
  details: () => [...mediaKeys.all, "detail"] as const,
  detail: (ownerId: string, albumId: string, mediaId: string) =>
    [...mediaKeys.details(), ownerId, albumId, mediaId] as const,
};

const MEDIA_PAGE_SIZE = 20;

export const useAlbumUploaders = (ownerId?: string, albumId?: string, options?: { enabled?: boolean }) => {
  const { data, isLoading, isSuccess, error } = useQuery({
    queryKey: mediaKeys.uploaders(ownerId!, albumId!),
    queryFn: async () => {
      if (!ownerId || !albumId) {
        throw new Error("Missing ownerId or albumId");
      }

      await authService.checkAuthentication();
      return mediaService.listAlbumUploaders(ownerId, albumId);
    },
    enabled: (options?.enabled ?? true) && !!ownerId && !!albumId,
    staleTime: Infinity,
  });

  return {
    uploaders: data ?? [],
    isLoading,
    isSuccess,
    error: error ? getErrorMessage(handleApiError(error)) : null,
  };
};

export const useMedia = (
  ownerId?: string,
  albumId?: string,
  sort: MediaSortOptions = DEFAULT_MEDIA_SORT,
  uploaderFilter?: MediaUploaderFilter | null
) => {
  const queryClient = useQueryClient();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<{
    totalBytes: number;
    uploadedBytes: number;
    totalFiles: number;
    currentFileIndex: number;
    currentFileName?: string;
    currentFileBytesUploaded?: number;
    currentFileBytesTotal?: number;
  } | null>(null);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const {
    data: indexData,
    isLoading: isIndexLoading,
    isError: isIndexError,
    error: indexError,
    refetch: refetchIndex,
  } = useQuery({
    queryKey: mediaKeys.index(ownerId!, albumId!, sort, uploaderFilter),
    queryFn: async () => {
      if (!ownerId || !albumId) {
        throw new Error("Missing ownerId or albumId");
      }
      await authService.checkAuthentication();
      return mediaService.listMediaIndex(ownerId, albumId, sort, uploaderFilter);
    },
    enabled: !!ownerId && !!albumId,
  });

  const {
    data,
    isLoading: isListLoading,
    error: listError,
    refetch: refetchList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<Media[], Error, InfiniteData<Media[]>>({
    queryKey: mediaKeys.list(ownerId!, albumId!, sort, uploaderFilter),
    queryFn: async ({ pageParam = 0 }) => {
      if (!ownerId || !albumId) {
        throw new Error("Missing ownerId or albumId");
      }

      await authService.checkAuthentication();

      return mediaService.listMedia(ownerId, albumId, pageParam as number, MEDIA_PAGE_SIZE, sort, uploaderFilter);
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < MEDIA_PAGE_SIZE) {
        return undefined; // No more pages
      }
      return allPages.length * MEDIA_PAGE_SIZE;
    },
    initialPageParam: 0,
    enabled: !!ownerId && !!albumId,
  });

  const isFiltered = (uploaderFilter?.length ?? 0) > 0;
  const { data: filteredTotal } = useQuery({
    queryKey: mediaKeys.count(ownerId!, albumId!, uploaderFilter),
    queryFn: async () => {
      if (!ownerId || !albumId) {
        throw new Error("Missing ownerId or albumId");
      }
      await authService.checkAuthentication();
      return mediaService.countMedia(ownerId, albumId, uploaderFilter);
    },
    enabled: !!ownerId && !!albumId && isFiltered,
  });

  // Flatten the pages data into a single array
  const loadedMedia = useMemo(() => {
    return data?.pages.flatMap((page) => page) ?? [];
  }, [data]);

  const media = useMemo(() => {
    if (!ownerId || !albumId || !indexData?.items) {
      albumDebug("media.hydrate skipped (no index yet)", {
        albumId,
        loadedMedia: loadedMedia.length,
      });
      return loadedMedia;
    }
    const startedAt = performance.now();
    const hydrated = hydrateMediaFromIndex(indexData.items, ownerId, albumId);
    const hydrateMs = Number((performance.now() - startedAt).toFixed(1));
    const overlayStartedAt = performance.now();
    const overlaid = overlayMediaById(hydrated, loadedMedia);
    let takenAtChanged = 0;
    const takenAtMismatches: Array<Record<string, unknown>> = [];
    if (loadedMedia.length > 0) {
      const stubById = new Map(hydrated.map((item) => [item.id, item]));
      for (const full of loadedMedia) {
        const stub = stubById.get(full.id);
        const stubTakenAt = stub?.metadata?.takenAt;
        const fullTakenAt = full.metadata?.takenAt;
        if (stubTakenAt !== fullTakenAt) {
          takenAtChanged += 1;
          if (takenAtMismatches.length < 8) {
            takenAtMismatches.push({
              id: full.id,
              filename: full.filename,
              contentType: full.contentType,
              indexTakenAt: stubTakenAt ?? null,
              listTakenAt: fullTakenAt ?? null,
            });
          }
        }
      }
    }
    albumDebugDuration("media.hydrate+overlay", startedAt, {
      albumId,
      indexItems: indexData.items.length,
      loadedMedia: loadedMedia.length,
      result: overlaid.length,
      hydrateMs,
      overlayMs: Number((performance.now() - overlayStartedAt).toFixed(1)),
      takenAtChanged,
      takenAtMismatches,
    });
    return overlaid;
  }, [albumId, indexData, loadedMedia, ownerId]);

  const isLoading = isIndexError ? isListLoading : isIndexLoading;
  const error = isIndexError ? (listError ?? indexError) : indexError;

  const uploadMultipleMediaMutation = useMutation({
    mutationFn: async (files: File[]) => {
      if (!ownerId || !albumId) {
        throw new Error("Missing ownerId or albumId");
      }

      await authService.checkAuthentication();

      // Start progress tracking (real bytes-based progress from XHR uploads)
      setUploadProgress(0);
      setUploadStatus({
        totalBytes: files.reduce((sum, f) => sum + (Number.isFinite(f.size) ? f.size : 0), 0),
        uploadedBytes: 0,
        totalFiles: files.length,
        currentFileIndex: 0,
        currentFileName: files[0]?.name,
        currentFileBytesUploaded: 0,
        currentFileBytesTotal: files[0]?.size ?? 0,
      });

      try {
        const result = await mediaService.uploadMultipleMedia(ownerId, albumId, files, (progress) => {
          setUploadStatus(progress);
          const percent =
            progress.totalBytes > 0 ? Math.round((progress.uploadedBytes / progress.totalBytes) * 100) : 100;
          setUploadProgress(Math.max(0, Math.min(100, percent)));
        });

        // Clear progress interval and set to 100%
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        setUploadProgress(100);
        setUploadStatus((prev) => {
          const totalBytes = prev?.totalBytes ?? 0;
          return prev
            ? { ...prev, uploadedBytes: totalBytes }
            : {
                totalBytes,
                uploadedBytes: totalBytes,
                totalFiles: files.length,
                currentFileIndex: Math.max(files.length - 1, 0),
              };
        });

        return result;
      } catch (error) {
        // Clear progress interval on error
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        setUploadProgress(0);
        setUploadStatus(null);
        throw error;
      }
    },
    onSuccess: (result: MultipleMediaUploadResult) => {
      if (result.successful.length > 0) {
        const selectedUserIds = new Set(
          (uploaderFilter ?? []).map((source) => source.slice(0, 36)).filter((id) => id.length === 36)
        );
        const matchingUploads =
          !uploaderFilter || uploaderFilter.length === 0
            ? result.successful
            : result.successful.filter((item) => selectedUserIds.has(item.userId));
        // Optimistically prepend only when our sort guarantees "new uploads first".
        if (matchingUploads.length > 0 && sort.sortBy === "uploadedAt" && sort.sortOrder === "desc") {
          queryClient.setQueryData<InfiniteData<Media[]>>(
            mediaKeys.list(ownerId!, albumId!, sort, uploaderFilter),
            (old) => {
              if (!old) {
                return {
                  pages: [matchingUploads],
                  pageParams: [0],
                };
              }

              // Add new items to the first page
              const newPages = [...old.pages];
              if (newPages.length > 0) {
                newPages[0] = [...matchingUploads, ...newPages[0]];
              } else {
                newPages.push(matchingUploads);
              }

              return {
                ...old,
                pages: newPages,
              };
            }
          );
        } else {
          queryClient.invalidateQueries({ queryKey: mediaKeys.listBase(ownerId!, albumId!), exact: false });
        }
        queryClient.invalidateQueries({ queryKey: mediaKeys.indexBase(ownerId!, albumId!), exact: false });
        queryClient.invalidateQueries({ queryKey: mediaKeys.uploaders(ownerId!, albumId!) });
      }

      // Invalidate album detail to refresh mediaCount
      queryClient.invalidateQueries({ queryKey: albumKeys.detail(ownerId!, albumId!) });
      // Invalidate albums list to refresh thumbnails
      queryClient.invalidateQueries({ queryKey: albumKeys.list(ownerId!) });
    },
    onError: (error) => {
      console.error("Error uploading media:", handleApiError(error));
    },
  });

  const deleteMediaMutation = useMutation({
    mutationFn: async (mediaId: string) => {
      if (!ownerId || !albumId) {
        throw new Error("Missing ownerId or albumId");
      }

      await authService.checkAuthentication();

      return mediaService.deleteMedia(ownerId, albumId, mediaId);
    },
    onSuccess: (_, mediaId) => {
      // Update all cached lists for this album (regardless of sorting).
      queryClient.setQueriesData<InfiniteData<Media[]>>(
        { queryKey: mediaKeys.listBase(ownerId!, albumId!), exact: false },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => page.filter((media) => media.id !== mediaId)),
          };
        }
      );
      queryClient.setQueriesData<MediaIndex>(
        { queryKey: mediaKeys.indexBase(ownerId!, albumId!), exact: false },
        (old) => withoutMediaIndexItem(old, mediaId)
      );
      queryClient.removeQueries({ queryKey: mediaKeys.detail(ownerId!, albumId!, mediaId) });
      queryClient.invalidateQueries({ queryKey: mediaKeys.uploaders(ownerId!, albumId!) });
      queryClient.invalidateQueries({ queryKey: mediaKeys.duplicateFingerprints(ownerId!, albumId!) });

      // Invalidate album detail to refresh mediaCount
      queryClient.invalidateQueries({ queryKey: albumKeys.detail(ownerId!, albumId!) });
      // Invalidate albums list to refresh thumbnails
      queryClient.invalidateQueries({ queryKey: albumKeys.list(ownerId!) });
    },
    onError: (error) => {
      console.error("Error deleting media:", handleApiError(error));
    },
  });

  // Cleanup progress interval on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  return {
    // Data
    media,
    hasMedia: media.length > 0,
    loadedMediaCount: loadedMedia.length,
    filteredTotal: isFiltered ? filteredTotal : undefined,

    // Loading states
    isLoading,
    isUploading: uploadMultipleMediaMutation.isPending,
    isDeleting: deleteMediaMutation.isPending,
    isFetchingNextPage,
    uploadProgress,
    uploadStatus,

    // Infinite scrolling
    fetchNextPage,
    hasNextPage,

    // Error
    error: error ? getErrorMessage(handleApiError(error)) : null,

    // Actions
    uploadMultipleMedia: uploadMultipleMediaMutation.mutateAsync,
    deleteMedia: deleteMediaMutation.mutateAsync,
    refreshMedia: async () => {
      await Promise.all([refetchIndex(), refetchList()]);
    },
    clearError: () => {
      queryClient.resetQueries({ queryKey: mediaKeys.list(ownerId!, albumId!, sort, uploaderFilter) });
      queryClient.resetQueries({ queryKey: mediaKeys.index(ownerId!, albumId!, sort, uploaderFilter) });
    },
    resetUploadProgress: useCallback(() => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setUploadProgress(0);
      setUploadStatus(null);
    }, []),

    // Query client for manual invalidation
    invalidateMedia: () => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.listBase(ownerId!, albumId!), exact: false });
      queryClient.invalidateQueries({ queryKey: mediaKeys.indexBase(ownerId!, albumId!), exact: false });
    },
    invalidateAlbums: () => queryClient.invalidateQueries({ queryKey: albumKeys.list(ownerId!) }),
  };
};
